import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useMonitorStore } from "@/stores/monitor-store";
import { ChevronDown, ChevronRight, Loader2, Play, Square } from "lucide-react";
import type { Concept } from "@/types/concept";
import { useCurrentUser, isPaidPlan, useUpdateUser } from "@/hooks/useCurrentUser";
import { UpgradeCard } from "@/components/UpgradeCard";

const INTERVAL_OPTIONS = [
  { label: "Daily", value: 1 },
  { label: "Every 3 days", value: 3 },
  { label: "Weekly", value: 7 },
  { label: "Every 2 weeks", value: 14 },
  { label: "Monthly", value: 30 },
];

const MAX_CITATIONS_OPTIONS = [50, 100, 250, 500];

interface Props {
  concept: Concept;
  onSettingsChange: (settings: {
    monitor_enabled?: boolean;
    monitor_interval_days?: number;
    monitor_max_citations?: number;
    monitor_auto_dismiss_threshold?: number | null;
  }) => void;
  onRunNow: () => void;
  onCancel: () => void;
  isSettingsUpdating: boolean;
}

export function MonitorSettingsPanel({
  concept,
  onSettingsChange,
  onRunNow,
  onCancel,
  isSettingsUpdating,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const { isRunning } = useMonitorStore();
  const { data: currentUser } = useCurrentUser();
  const updateUser = useUpdateUser();

  // Gate: must be AFTER all hooks (React rules)
  if (!isPaidPlan(currentUser?.plan)) {
    return (
      <UpgradeCard
        feature="Living Literature Monitor"
        description="Automatically track new publications matching your concept and get notified when relevant papers are published."
      />
    );
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="rounded-md border">
      <button
        className="flex w-full items-center gap-2 p-3 text-sm font-medium hover:bg-muted/50"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <ChevronDown className="size-4" />
        ) : (
          <ChevronRight className="size-4" />
        )}
        Literature Monitor
        {concept.monitor_enabled && (
          <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900 dark:text-green-300">
            Active
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          Last run: {formatDate(concept.last_monitor_run)}
        </span>
      </button>

      {isOpen && (
        <div className="border-t p-4 space-y-4">
          {/* Auto-monitor toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="monitor-enabled" className="text-sm">
              Auto-monitor for new papers
            </Label>
            {isSettingsUpdating && (
              <span role="status" className="ml-auto mr-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />Saving…
              </span>
            )}
            <Switch
              id="monitor-enabled"
              checked={concept.monitor_enabled}
              onCheckedChange={(checked) =>
                onSettingsChange({ monitor_enabled: checked })
              }
              disabled={isSettingsUpdating}
            />
          </div>

          {/* Interval */}
          <div className="flex items-center justify-between">
            <Label className="text-sm">Check interval</Label>
            <select
              className="rounded-md border px-2 py-1 text-sm bg-background"
              value={concept.monitor_interval_days}
              onChange={(e) =>
                onSettingsChange({ monitor_interval_days: Number(e.target.value) })
              }
              disabled={isSettingsUpdating}
            >
              {INTERVAL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Max citations */}
          <div className="flex items-center justify-between">
            <Label className="text-sm">Max citations per paper</Label>
            <select
              className="rounded-md border px-2 py-1 text-sm bg-background"
              value={concept.monitor_max_citations}
              onChange={(e) =>
                onSettingsChange({ monitor_max_citations: Number(e.target.value) })
              }
              disabled={isSettingsUpdating}
            >
              {MAX_CITATIONS_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {/* Auto-dismiss threshold */}
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground">Auto-dismiss below</label>
            <select
              className="rounded border border-border bg-background px-2 py-1 text-xs"
              value={concept.monitor_auto_dismiss_threshold ?? "off"}
              onChange={(e) => {
                const v = e.target.value;
                onSettingsChange({
                  monitor_auto_dismiss_threshold: v === "off" ? null : Number(v),
                });
              }}
            >
              <option value="off">Off</option>
              <option value="20">Score &lt; 20</option>
              <option value="30">Score &lt; 30</option>
              <option value="40">Score &lt; 40</option>
            </select>
          </div>

          {/* Email digest frequency */}
          <div className="flex items-center justify-between">
            <Label className="text-sm">Email digest</Label>
            <select
              className="rounded-md border px-2 py-1 text-sm bg-background"
              value={currentUser?.digest_frequency ?? "weekly"}
              onChange={(e) => updateUser.mutate({ digest_frequency: e.target.value })}
            >
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
              <option value="off">Off</option>
            </select>
          </div>

          {/* Last/Next run info */}
          <div className="flex gap-6 text-xs text-muted-foreground">
            <span>Last: {formatDate(concept.last_monitor_run)}</span>
            <span>Next: {formatDate(concept.next_monitor_run)}</span>
          </div>

          {/* Run Now / Cancel */}
          <div className="flex gap-2">
            {isRunning ? (
              <Button variant="outline" size="sm" onClick={onCancel}>
                <Square className="mr-1 size-3" />
                Cancel
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={onRunNow}>
                <Play className="mr-1 size-3" />
                Run Now
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
